import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { keyframes } from 'tss-react';
import { makeStyles } from 'tss-react/mui';
import Icon from '../../../base/icons/components/Icon';
import { IconRemoteControlStart } from '../../../base/icons/svg';
import { getParticipantDisplayName } from '../../../base/participants/functions';
import { cancelRequest, requestRemoteControl } from '../../actions';
import { getControllableParticipantOnStage } from '../../functions';
import { useVideoRect } from './useVideoRect';
const useStyles = makeStyles()(theme => {
    return {
        anchor: {
            position: 'absolute',
            display: 'flex',
            justifyContent: 'center',
            zIndex: 3,
            pointerEvents: 'none'
        },
        // The offer sits on the shared screen, not in a menu, because that is
        // where someone is already looking when they think "I wish I could just
        // do this myself". It stays quiet until the pointer is near it.
        button: {
            pointerEvents: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
            margin: theme.spacing(3),
            padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
            border: `1px solid ${theme.palette.ui03}66`,
            borderRadius: '999px',
            background: `${theme.palette.ui01}D9`,
            backdropFilter: 'blur(16px)',
            boxShadow: '0 8px 28px -10px rgba(0, 0, 0, .7)',
            color: theme.palette.text01,
            ...theme.typography.bodyShortBold,
            cursor: 'pointer',
            opacity: 0.75,
            transition: 'opacity .14s ease-out, border-color .14s ease-out, transform .14s ease-out',
            '&:hover': {
                opacity: 1,
                borderColor: theme.palette.action01,
                transform: 'translateY(-1px)'
            },
            '&:focus-visible': {
                opacity: 1,
                outline: `2px solid ${theme.palette.action01}`,
                outlineOffset: '2px'
            },
            '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
                '&:hover': {
                    transform: 'none'
                }
            }
        },
        icon: {
            display: 'flex',
            '& svg': {
                fill: theme.palette.action01
            }
        },
        waiting: {
            pointerEvents: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
            margin: theme.spacing(3),
            padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
            border: `1px solid ${theme.palette.ui03}66`,
            borderRadius: '999px',
            background: `${theme.palette.ui01}F2`,
            backdropFilter: 'blur(16px)',
            boxShadow: '0 8px 28px -10px rgba(0, 0, 0, .7)',
            color: theme.palette.text01,
            ...theme.typography.bodyShortRegular
        },
        pulse: {
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: theme.palette.action01,
            animation: `${keyframes `
                0%, 100% { opacity: 1; }
                50% { opacity: .3; }
            `} 1.4s ease-in-out infinite`,
            '@media (prefers-reduced-motion: reduce)': {
                animation: 'none'
            }
        },
        cancel: {
            appearance: 'none',
            border: 0,
            background: 'none',
            padding: 0,
            marginLeft: theme.spacing(1),
            color: theme.palette.link01,
            font: 'inherit',
            ...theme.typography.bodyShortBold,
            cursor: 'pointer',
            '&:hover': {
                textDecoration: 'underline'
            },
            '&:focus-visible': {
                outline: `2px solid ${theme.palette.action01}`,
                outlineOffset: '2px',
                borderRadius: '4px'
            }
        }
    };
});
/**
 * The offer to take control of the screen currently on stage.
 *
 * @returns {ReactElement|null}
 */
const RequestControlButton = () => {
    const { classes } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const rect = useVideoRect();
    const target = useSelector(getControllableParticipantOnStage);
    const { controlled, requestedParticipant } = useSelector((state) => state['features/remote-control'].controller);
    const targetName = useSelector((state) => (target ? getParticipantDisplayName(state, target) : ''));
    const onRequest = useCallback(() => {
        target && dispatch(requestRemoteControl(target));
    }, [dispatch, target]);
    const onCancel = useCallback(() => {
        dispatch(cancelRequest());
    }, [dispatch]);
    // Once a session is running the control bar takes over, and nothing here
    // should compete with it.
    if (!target || controlled || !rect) {
        return null;
    }
    const waiting = requestedParticipant === target;
    return (React.createElement("div", { className: classes.anchor, style: {
            left: rect.left,
            top: rect.top,
            width: rect.width
        } }, waiting
        ? (React.createElement("div", { className: classes.waiting },
            React.createElement("span", { className: classes.pulse }),
            React.createElement("span", null, t('remoteControl.waitingFor', { user: targetName })),
            React.createElement("button", { className: classes.cancel, onClick: onCancel, type: 'button' }, t('remoteControl.cancelRequest'))))
        : (React.createElement("button", { className: classes.button, onClick: onRequest, type: 'button' },
            React.createElement("span", { className: classes.icon },
                React.createElement(Icon, { size: 18, src: IconRemoteControlStart })),
            t('remoteControl.requestControl')))));
};
export default RequestControlButton;
